import ButtonGroup from '@atlaskit/button/button-group';

import IconButton from '@atlaskit/button/icon/button';

import Heading from '@atlaskit/heading/heading';

import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';

import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';

import CloseIcon from '@atlaskit/icon/core/cross';

import { Flex } from '@atlaskit/primitives/compiled/flex';

import { Stack } from '@atlaskit/primitives/compiled/stack';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { type RefObject, useEffect } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { messages } from '../messages';

type CarouselWrapperProps = {
	children: JSX.Element;
	containerRef: RefObject<HTMLElement>;
	numCardsToShow: number;
	numTotalCards: number;
	firstIndexOnScreen: number;
	lastIndexOnScreen: number;
	handleNextButtonClick: () => void;
	handlePrevButtonClick: () => void;
	closeCarousel: () => void;
	cardsListForAnalytics?: string[];
};

export const CarouselWrapper = (props: CarouselWrapperProps): JSX.Element => {
	const intl = useIntl();
	const {
		children,
		containerRef,
		numCardsToShow,
		numTotalCards,
		firstIndexOnScreen,
		lastIndexOnScreen,
		handleNextButtonClick,
		handlePrevButtonClick,
		closeCarousel,
		cardsListForAnalytics,
	} = props;

	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

	const handleCloseCarousel = () => {
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'carouselCloseButton',
			actionSubjectId: 'townsquareOnboardingCarouselCloseButton',
			attributes: { cards: cardsListForAnalytics },
		});
		closeCarousel();
	};

	useEffect(() => {
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'viewed',
			actionSubject: 'carousel',
			actionSubjectId: 'townsquareOnboardingCarousel',
			attributes: { cards: cardsListForAnalytics },
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Stack space="space.150" testId="townsquare-onboarding-widget" ref={containerRef}>
			<Flex justifyContent="space-between" alignItems="center">
				<Heading size="small">
					<FormattedMessage {...messages.carouselHeading} />
				</Heading>
				<ButtonGroup>
					{numCardsToShow < numTotalCards && (
						<>
							<IconButton
								appearance="subtle"
								icon={ArrowLeftIcon}
								label={intl.formatMessage(messages.prevButtonLabel)}
								isDisabled={firstIndexOnScreen === 0}
								isTooltipDisabled={firstIndexOnScreen === 0}
								onClick={handlePrevButtonClick}
								testId={'prev-button'}
							/>
							<IconButton
								appearance="subtle"
								icon={ArrowRightIcon}
								label={intl.formatMessage(messages.nextButtonLabel)}
								isDisabled={lastIndexOnScreen === numTotalCards}
								isTooltipDisabled={lastIndexOnScreen === numTotalCards}
								onClick={handleNextButtonClick}
								testId={'next-button'}
							/>
						</>
					)}
					<IconButton
						appearance="subtle"
						icon={CloseIcon}
						label={intl.formatMessage(messages.closeButtonLabel)}
						isTooltipDisabled={false}
						onClick={handleCloseCarousel}
						testId={'close-button'}
					/>
				</ButtonGroup>
			</Flex>
			{children}
		</Stack>
	);
};
